import type { FC, ReactElement, ReactNode } from 'react';
import { useRef } from 'react';

import clsx from 'clsx';

import Skeleton from '@polyai/ui/components/atoms/Skeleton';
import type { TooltipProps } from '@polyai/ui/components/atoms/Tooltip';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import type themeSpacing from '@polyai/ui/styles/config/spacing';

import * as Styled from './JupiterCard.styled';

export interface BaseColumnProps {
  icon?: ReactNode;
  iconWrapperClassName?: string;
  customIcon?: ReactNode;
  title: ReactElement;
  titleTooltip?: ReactNode;
  titleTooltipProps?: Partial<TooltipProps>;
  titleTooltipDisableUnderline?: boolean;
  value: ReactNode;
  valueTooltip?: ReactNode;
  valueTooltipProps?: Partial<TooltipProps>;
  valueTooltipDisableUnderline?: boolean;
  titleValueGap?: keyof typeof themeSpacing;
  isLoading?: boolean;
  className?: string;
}

const BaseColumn: FC<BaseColumnProps> = ({
  icon,
  iconWrapperClassName,
  customIcon,
  title,
  titleTooltip,
  titleTooltipProps,
  titleTooltipDisableUnderline,
  titleValueGap,
  value,
  valueTooltip,
  valueTooltipProps,
  valueTooltipDisableUnderline,
  isLoading,
  className,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const Title = titleTooltip ? (
    <Tooltip content={titleTooltip} placement="bottom" {...titleTooltipProps}>
      <Styled.TooltipWrapper $underline={!titleTooltipDisableUnderline}>
        {title}
      </Styled.TooltipWrapper>
    </Tooltip>
  ) : (
    title
  );

  const Value = valueTooltip ? (
    <Tooltip content={valueTooltip} placement="bottom" {...valueTooltipProps}>
      <Styled.TooltipWrapper $underline={!valueTooltipDisableUnderline}>
        {value}
      </Styled.TooltipWrapper>
    </Tooltip>
  ) : (
    value
  );

  return (
    <Styled.CardContainer ref={containerRef} className={className} {...props}>
      {customIcon ||
        (icon && (
          <span
            className={clsx(
              `flex items-center rounded-full p-xs1 shrink-0 bg-background-accent text-icon-secondary transition-colors duration-200`,
              iconWrapperClassName,
            )}
          >
            {icon}
          </span>
        ))}
      <Styled.CardTextContainer $gap={titleValueGap}>
        {Title}
        {isLoading ? (
          <Styled.SkeletonContainer>
            <Skeleton />
          </Styled.SkeletonContainer>
        ) : (
          Value
        )}
      </Styled.CardTextContainer>
    </Styled.CardContainer>
  );
};

export default BaseColumn;
